"use client";

import Link from "@casino/client/components/Link";
import cn from "@casino/external/classNames";
import useGameCardSize from "@casino/legacy/client/shared/game/GameCard/useGameCardSize";
import Img from "@casino/legacyUi/Img";
import type { GameCardTranslations } from "@casino/shared/components/GameCard/translations";
import {
  GameThumb as BaseGameThumb,
  type CardThumbnailBadge,
} from "@workspace/frontend-ui";
import ComingSoonGameThumb from "./ComingSoonGameThumb";
import GameProvider from "./GameProvider";
import Hover from "./Hover";
import useBadge from "./hooks/useBadge";

type Props = {
  game: GameFragment;
  eager?: boolean;
  href?: string;
  badge?: CardThumbnailBadge;
  gameCardTranslations?: GameCardTranslations;
  size?: "auto" | "normal";
  // Overrides the default portrait dimensions from useGameCardSize — e.g. a
  // larger card for a featured row. CardThumbnail keeps its own fixed
  // 210:282 aspect ratio regardless, so pick numbers matching that ratio.
  dimensions?: { width: number; height: number };
  isComingSoon?: boolean;
};

const GameThumb: FC<Props> = ({
  game,
  eager,
  href,
  badge,
  gameCardTranslations,
  size = "normal",
  dimensions,
  isComingSoon,
}) => {
  const gameHref = href ?? game.href ?? "";
  const defaultDimensions = useGameCardSize();
  const { width, height } = dimensions ?? defaultDimensions;
  const defaultBadge = useBadge(game, gameCardTranslations, !badge);
  const image = (
    <Img
      preload={eager}
      // Lazy so the rows below the banner don't compete with the LCP image.
      loading={eager ? undefined : "lazy"}
      src={game.thumbUrlTab || game.thumbUrl || ""}
      alt={game.name ?? ""}
      width={width}
      height={height}
      dpr={1.5}
      quality={100}
      fit="crop"
      crop="faces"
      className={size === "auto" ? "w-full" : undefined}
    />
  );

  if (isComingSoon) {
    return <ComingSoonGameThumb game={game} image={image} size={size} />;
  }

  return (
    <BaseGameThumb
      game={game}
      link={
        <Link
          href={gameHref}
          prefetch={false}
          className={cn("block", size === "auto" ? "w-full" : "shrink-0 w-fit")}
        >
          {image}
        </Link>
      }
      providerLink={<GameProvider game={game} />}
      hover={
        <Hover
          game={game}
          gameHref={gameHref}
          translations={gameCardTranslations}
        />
      }
      badge={badge ?? defaultBadge ?? undefined}
      size={size}
    />
  );
};

export default GameThumb;
